import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AISystemsService } from 'services/AISystemService';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import LaunchIcon from '@mui/icons-material/Launch';
import { Utils } from 'shared/helpers';

interface ILinkedAISystems {
  linkedSystems: IAISystem[];
  trainingId: string;
  reloadDetail: () => void;
}

const LinkedAISystems: React.FC<ILinkedAISystems> = (props) => {
  const { linkedSystems, trainingId, reloadDetail } = props;

  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [records, setRecords] = useState<IAISystem[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [isLinking, setIsLinking] = useState('');

  // API Calls
  const loadAISystems = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: '0',
      limit: '1000',
      search: '',
    }).toString();

    AISystemsService.getAISystems(queryString)
      .then((response) => {
        const { aiSystems } = response.data.data;
        /* eslint no-underscore-dangle: 0 */
        setRecords(aiSystems.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const linkRecord = (payload: any, callback: any): void => {
    TrainingPorgramsService.linkAISystem(trainingId, payload)
      .then((response) => {
        callback(response);
      })
      .catch((e) => {
        Utils.handleErrorResponse(e);
        callback(false);
      });
  };

  // UI Actions
  const actionOnLink = (status: string, id: string): void => {
    setIsLinking(status);
    linkRecord(
      {
        aisystemId: id,
        action: status,
      },
      (response: any) => {
        setIsLinking('');
        setSelectedRecordId('');
        if (typeof response === 'object') {
          reloadDetail();
        }
      }
    );
  };

  // Hooks
  useEffect(() => {
    loadAISystems();
  }, []);

  // UI Elements
  return (
    <Box className="app-plain-card mt-4">
      <Box className="title">
        <Typography variant="h4">Linked AI Systems</Typography>
      </Box>

      <Box className="content">
        {isLoadingRecords && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}

        {!isLoadingRecords && (
          <>
            <Stack direction="row" spacing={4} alignItems="center">
              <AppSelectWithKeys
                className
                label=""
                onChange={(e: any) => {
                  setSelectedRecordId(e.target.value);
                }}
                items={records.filter(
                  (r) => !linkedSystems.some((ls) => ls._id === r._id)
                )}
                titleKey="solutionName"
                valueKey="_id"
              />
              <AppPrimaryButtonOutlined
                isLoading={isLinking === 'link'}
                disabled={!selectedRecordId}
                title="Link"
                onClick={() => {
                  actionOnLink('link', selectedRecordId);
                }}
              />
            </Stack>
            {linkedSystems.length > 0 && (
              <List
                style={{
                  border: '1px solid lightgray',
                  marginTop: '10px',
                  borderRadius: '5px',
                }}
              >
                {linkedSystems.map((aiSystem, index) => (
                  <>
                    <ListItem>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: '100%' }}
                      >
                        <Stack>
                          <Link to={`/ai-systems/records/${aiSystem.id}`}>
                            <Stack
                              className="text-primary mb-2"
                              alignItems="center"
                              direction="row"
                              spacing={2}
                            >
                              <LaunchIcon />
                              <Typography className="f18 fw-6">
                                {aiSystem.solutionName}
                              </Typography>
                            </Stack>
                          </Link>
                          <Stack direction="row" spacing={2}>
                            <Chip
                              size="small"
                              label={`Risk: ${aiSystem.riskTier}`}
                            />
                            <Chip
                              size="small"
                              label={aiSystem.approvalStatus}
                            />
                            {aiSystem.lifecycleStage && (
                              <Chip
                                size="small"
                                label={`Risk: ${aiSystem.lifecycleStage}`}
                              />
                            )}
                          </Stack>
                        </Stack>
                        <AppPrimaryButton
                          isLoading={selectedRecordId === aiSystem._id}
                          onClick={() => {
                            setSelectedRecordId(aiSystem._id);
                            actionOnLink('unlink', aiSystem._id);
                          }}
                          className="btn-danger btn-small"
                          title="Unlink"
                        />
                      </Stack>
                    </ListItem>
                    {index < linkedSystems.length - 1 && <Divider />}
                  </>
                ))}
              </List>
            )}
            {linkedSystems.length === 0 && (
              <Box className="empty-content">
                <Typography className="f18">No AI Systems linked</Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LinkedAISystems;
